<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main>
        <section class="pt-5">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div
              class="flex flex-col gap-4 mt-8 mb-6 md:flex-row md:items-center md:justify-between"
            >
              <h2 class="font-semibold text-heading-5">Dosen</h2>
              <div class="relative w-full md:w-1/3">
                <div
                  class="absolute top-0 bottom-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none"
                >
                  <MagnifyingGlassVue />
                </div>
                <input
                  v-model="search"
                  type="text"
                  autocomplete="off"
                  placeholder="Cari Nama"
                  class="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div class="pt-4 border-t border-gray-3">
              <div class="flex flex-col">
                <div
                  class="grid flex-grow grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4"
                >
                  <div
                    v-for="(dosen, index) in paginatedDosen"
                    :key="dosen.id || index"
                    class="flex flex-col sm:flex-row items-center border border-gray-3 rounded-[16px] p-4 cursor-pointer bg-white hover:-translate-y-1 transition-all gap-3 h-48 sm:h-32"
                    @click="viewProfile(dosen.id)"
                  >
                    <div
                      class="flex items-center justify-center w-20 h-20 overflow-hidden bg-white border rounded-lg sm:w-24 sm:h-24 sm:mr-4 shrink-0"
                    >
                      <img
                        :src="getFotoUrl(dosen.foto, dosen.nama)"
                        alt="avatar"
                        class="object-cover w-full h-full"
                      />
                    </div>
                    <div
                      class="flex flex-col justify-center flex-1 min-h-0 space-y-1 overflow-hidden text-center sm:text-left mobile-font"
                    >
                      <h3
                        class="text-sm font-semibold leading-tight sm:text-base text-dark line-clamp-2 mobile-font"
                      >
                        {{ dosen.nama }}
                      </h3>
                      <h5
                        class="text-xs font-semibold leading-tight sm:text-sm text-dark line-clamp-1 mobile-font"
                      >
                        NIP. {{ dosen.nip }}
                      </h5>
                      <p
                        class="text-xs leading-tight text-gray-600 sm:text-sm line-clamp-2 mobile-font"
                      >
                        Email: {{ dosen.email || "-" }}<br />
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Pagination - Always at bottom -->
                <div
                  class="flex justify-center pt-4 mt-4 border-t border-gray-200"
                  v-if="totalPages > 1"
                >
                  <div class="flex flex-wrap justify-center gap-1">
                    <button
                      v-for="p in totalPages"
                      :key="p"
                      @click="goToPage(p)"
                      :class="[
                        'px-3 py-2 border border-gray-300 text-sm rounded transition-colors duration-200',
                        p === page
                          ? 'bg-red-900 text-white border-red-900'
                          : 'bg-white text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-900',
                      ]"
                    >
                      {{ p }}
                    </button>
                  </div>
                </div>
              </div>

              <Loading
                v-if="isLoading"
                :active="isLoading"
                :can-cancel="false"
                :is-full-page="false"
              />
              <ProfilDosen
                v-else
                v-model:isOpen="isModalOpen"
                :dosendata="viewDosen"
              />
            </div>
          </div>
        </section>
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import ProfilDosen from "@/Components/Web/ProfilDosen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
const props = defineProps({
  dosen: {
    type: Array,
    required: true,
  },
});

const search = ref("");
const page = ref(1);
const perPage = 9;
const isModalOpen = ref(false);
const viewDosen = ref(null);
const isLoading = ref(false);

const filteredDosen = computed(() => {
  // Pastikan props.dosen adalah array
  const data = Array.isArray(props.dosen)
    ? props.dosen
    : props.dosen.data || [];
  if (!search.value) return data;
  const q = search.value.toLowerCase();
  return data.filter(
    (d) =>
      d.nama.toLowerCase().includes(q) ||
      (d.nip && d.nip.toLowerCase().includes(q)) ||
      (d.nidn && d.nidn.toLowerCase().includes(q)) ||
      (d.email && d.email.toLowerCase().includes(q))
  );
});

const paginatedDosen = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredDosen.value.slice(start, start + perPage);
});

const totalPages = computed(() =>
  Math.ceil(filteredDosen.value.length / perPage)
);

function goToPage(p) {
  page.value = p;
}

function getFotoUrl(foto, nama = "User") {
  if (!foto) {
    const initials = nama
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=e5e7eb&color=6b7280&size=128`;
  }
  if (/^https?:\/\//.test(foto) || foto.startsWith("/storage")) {
    return foto;
  }
  return "/storage/" + foto;
}
const viewProfile = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewDosen.value = null;
  try {
    const response = await fetch(`/vdosen/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    const data = await response.json();
    viewDosen.value = data.datadosen;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
/* Line clamp utilities for text truncation */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Tight line height for better text spacing */
.leading-tight {
  line-height: 1.25;
}

/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 500;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont,
      sans-serif;
    font-weight: inherit;
  }
}

/* Alternative: You can use different system fonts for mobile */
/*
@media (max-width: 639px) {
  .mobile-font {
    font-family: -apple-system, BlinkMacSystemFont, 'San Francisco', 'Helvetica Neue', sans-serif;
    font-weight: 400;
    letter-spacing: 0.01em;
  }
}
*/

/* Ensure consistent hover animations */
.hover\:-translate-y-1:hover {
  transform: translateY(-0.25rem);
}

/* Additional spacing utilities */
.space-y-1 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.25rem;
}

.space-y-2 > :not([hidden]) ~ :not([hidden]) {
  margin-top: 0.5rem;
}
</style>
